import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, useScroll, useSpring } from 'framer-motion';
import { ArrowLeft, MapPin, Calendar, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';

import { Record } from '../types/records';
import GoogleAdUnit from '../components/common/GoogleAdUnit';

import { getRecordById } from '../services/recordService';
import { trackUniqueStat } from '../services/achievementService';

const BlogDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [record, setRecord] = useState<Record | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const { scrollYProgress } = useScroll();
    const scaleX = useSpring(scrollYProgress, {
        stiffness: 100,
        damping: 30,
        restDelta: 0.001
    });

    useEffect(() => {
        if (!id) {
            setError("İçerik ID'si bulunamadı.");
            setLoading(false);
            return;
        }

        try {
            const foundRecord = getRecordById(id);
            console.log("BLOG DETAIL RECORD >>", foundRecord);
            console.log("COVER IMAGE >>", foundRecord?.coverImage);
            console.log("COVER IMAGE LENGTH >>", foundRecord?.coverImage?.length);

            if (foundRecord) {
                setRecord(foundRecord);

                // Başarım Takibi: Etkinlik Avcısı
                trackUniqueStat('viewedEvents', id, 5, 'event-hunter').then(unlocked => {
                    if (unlocked) {
                        alert("Tebrikler! 'Etkinlik Avcısı' başarımını kazandınız! 🎫");
                    }
                });
            } else {
                setError("Aradığınız içerik bulunamadı.");
            }
        } catch (e) {
            setError("İçerik yüklenirken bir hata oluştu.");
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [id]);

    if (loading) {
        return <div className="flex justify-center items-center h-screen bg-gray-50 dark:bg-gray-900 text-white">Yükleniyor...</div>;
    }

    if (error || !record) {
        return (
            <div className="flex flex-col justify-center items-center h-screen bg-gray-50 dark:bg-gray-900 text-center px-4">
                <AlertTriangle size={64} className="text-red-500 mb-4" />
                <h1 className="text-3xl font-bold text-gray-800 dark:text-white mb-2">{error || "İçerik Bulunamadı"}</h1>
                <p className="text-gray-600 dark:text-gray-400 mb-6">Aradığınız sayfa mevcut olmayabilir veya silinmiş olabilir.</p>
                <Link
                    to="/"
                    className="px-6 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                >
                    Ana Sayfaya Dön
                </Link>
            </div>
        );
    }

    const { title, summary, content, coverImage, galleryImages, category, status, cityName, districtName, lastUpdated, affiliateLinks } = record;

    return (
        <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
            <motion.div className="fixed top-0 left-0 right-0 h-1 bg-blue-600 origin-left z-50" style={{ scaleX }} />

            {/* Article Container - Magazine Style */}
            <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Back Button */}
                <div className="mb-6">
                    <Link to="/blog" className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:underline text-sm font-medium">
                        <ArrowLeft size={16} className="mr-1.5" />
                        Tüm İçeriklere Geri Dön
                    </Link>
                </div>

                {/* Header Section */}
                <motion.header
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="mb-8"
                >
                    {/* Category & Status Badges */}
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                        <span className="inline-block px-3 py-1 text-xs font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 rounded-full">
                            {category}
                        </span>
                        <span className="inline-block px-3 py-1 text-xs font-bold uppercase text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-full">
                            {status}
                        </span>
                    </div>

                    {/* Title */}
                    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-4">
                        {title}
                    </h1>

                    {/* Main Content Layout with Sidebar - Moved outside header to fix nesting */}

                    {/* Summary */}
                    <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                        {summary}
                    </p>

                    {/* Meta Info */}
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500 dark:text-gray-400 pb-6 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center">
                            <MapPin size={16} className="mr-1.5 text-gray-400" />
                            <span>{districtName ? `${districtName}, ${cityName}` : cityName}</span>
                        </div>
                        <div className="flex items-center">
                            <Calendar size={16} className="mr-1.5 text-gray-400" />
                            <span>{format(new Date(lastUpdated), 'dd MMMM yyyy')}</span>
                        </div>
                    </div>
                </motion.header>

                {/* Main Content Layout with Sidebar */}
                <div className="lg:flex lg:gap-8">
                    {/* Left Column: Article Content */}
                    <div className="lg:w-3/4">

                        {/* Cover Image - Constrained Height */}
                        {coverImage && coverImage.trim() !== '' && (
                            <motion.figure
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.2, duration: 0.5 }}
                                className="mb-10"
                            >
                                <div className="relative w-full h-64 sm:h-80 lg:h-96 rounded-2xl overflow-hidden shadow-2xl bg-gray-200 dark:bg-gray-800">
                                    <img
                                        src={coverImage}
                                        alt={title}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            </motion.figure>
                        )}

                        {/* Content - Reading Column */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.4, duration: 0.5 }}
                            className="mb-12"
                        >
                            {content && content.trim().length > 0 ? (
                                <div className="prose prose-lg dark:prose-invert max-w-none prose-headings:font-bold prose-p:leading-relaxed prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-a:text-blue-600 dark:prose-a:text-blue-400">
                                    <div dangerouslySetInnerHTML={{ __html: content }} />
                                </div>
                            ) : (
                                <p className="text-center text-gray-500 dark:text-gray-400 italic py-12">
                                    Bu içerik için detaylı bir açıklama bulunmuyor.
                                </p>
                            )}
                        </motion.div>

                        {/* Gallery - Elegant Grid */}
                        {galleryImages && galleryImages.length > 0 && (
                            <section className="mb-16">
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Galeri</h2>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                    {galleryImages.filter(img => img && img.trim() !== '').map((image, index) => (
                                        <motion.div
                                            key={index}
                                            whileHover={{ scale: 1.03 }}
                                            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                                            className="relative aspect-square rounded-xl overflow-hidden shadow-lg bg-gray-200 dark:bg-gray-800 cursor-pointer"
                                        >
                                            <img
                                                src={image}
                                                alt={`${title} galeri ${index + 1}`}
                                                className="w-full h-full object-cover hover:opacity-90 transition-opacity"
                                            />
                                        </motion.div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* CTA Section */}
                        <section className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-gray-800 dark:to-gray-700 p-8 rounded-2xl text-center shadow-lg">
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                                Bu şehir için sana özel bir gezi planı hazırlayalım mı?
                            </h3>
                            <Link
                                to={`/start-plan?destination=${cityName}`}
                                className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-3 rounded-full transition-all shadow-md hover:shadow-xl transform hover:-translate-y-0.5"
                            >
                                Bu Şehir İçin Plan Oluştur
                            </Link>
                        </section>

                        {/* Affiliate Links Section */}
                        {affiliateLinks && affiliateLinks.length > 0 && (
                            <section className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-800">
                                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 text-center">
                                    Bilet ve Rezervasyon Seçenekleri
                                </h3>
                                <div className="flex flex-wrap justify-center gap-4">
                                    {affiliateLinks.map((link, index) => (
                                        <a
                                            key={index}
                                            href={link.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`
                                            flex items-center px-6 py-3 rounded-xl font-semibold text-white transition-transform hover:-translate-y-1 shadow-lg
                                            ${link.provider === 'obilet' ? 'bg-[#ff0000]' :
                                                    link.provider === 'booking' ? 'bg-[#003580]' :
                                                        'bg-green-600'}
                                        `}
                                        >
                                            {link.label || 'Rezervasyon Yap'}
                                            <ArrowLeft className="ml-2 rotate-180" size={16} />
                                        </a>
                                    ))}
                                </div>
                            </section>
                        )}
                    </div>

                    {/* Right Column: Sticky Ad Slot */}
                    <aside className="hidden lg:block lg:w-1/4 pl-4">
                        <div className="sticky top-24">
                            <div className="text-xs text-gray-400 text-center mb-2 uppercase tracking-wide">Reklam</div>
                            <GoogleAdUnit />
                        </div>
                    </aside>
                </div>
            </article>
        </div>
    );
};

export default BlogDetailPage;